import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { uploadToBucket, deleteFromBucket } from '../lib/storage'
import type { ProductFile } from '../lib/database.types'

const FILES_KEY = (productId: string) => ['product_files', productId]
const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100 MB

export function useProductFiles(productId: string | undefined) {
  return useQuery({
    queryKey: FILES_KEY(productId ?? ''),
    enabled: !!productId,
    queryFn: async (): Promise<ProductFile[]> => {
      const { data, error } = await supabase
        .from('product_files')
        .select('*')
        .eq('product_id', productId!)
        .order('created_at', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useUploadProductFile(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File): Promise<ProductFile> => {
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is 100 MB.`)
      }
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')

      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'bin'
      const fileType = ['stl', '3mf', 'gcode', 'obj'].includes(ext) ? ext : 'other'

      const path = await uploadToBucket('model-files', productId, file.name, file)

      const { data, error } = await supabase
        .from('product_files')
        .insert({
          user_id: user.id,
          product_id: productId,
          file_path: path,
          file_name: file.name,
          file_type: fileType,
          size_bytes: file.size,
        })
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILES_KEY(productId) })
    },
  })
}

export function useDeleteProductFile(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (pf: ProductFile): Promise<void> => {
      await deleteFromBucket('model-files', pf.file_path)
      const { error } = await supabase.from('product_files').delete().eq('id', pf.id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FILES_KEY(productId) })
    },
  })
}
